import type { Theme } from '../../shared/themes';
import { rgba, shade } from './palette';
import { roundRect } from './sprites';

export interface RiderStyle {
  frame: string;
  kit: string;
  helmet: string;
  skin: string;
}

export const RIDER_STYLES: Record<string, RiderStyle> = {
  you: { frame: '#f2f4f8', kit: '#ff4f7a', helmet: '#ffe14d', skin: '#c98a5e' },
  rival: { frame: '#8be0ff', kit: '#4dffe1', helmet: '#7cf7ff', skin: '#a8703f' },
  ghost: { frame: '#ffffff', kit: '#ffffff', helmet: '#ffffff', skin: '#ffffff' },
};

/** Bike + rider height in world units. Keeps the sprite tied to the road scale. */
export const RIDER_HEIGHT = 560;

export interface RiderDrawOptions {
  x: number;
  /** Screen y of the contact patch (already raised by air height). */
  y: number;
  unitPx: number;
  /** -1..1 body lean. */
  lean: number;
  /** 0..1 through the current trick, or 0. */
  trickPhase: number;
  trickName: string | null;
  airborne: boolean;
  /** 0..1, fades the bike while invulnerable after a crash. */
  ghostly: number;
  stunned: boolean;
  sending: boolean;
  speedRatio: number;
  theme: Theme;
  timeS: number;
  style: RiderStyle;
  /** Ghost replays draw as a translucent silhouette. */
  silhouette?: boolean;
}

export function drawRider(ctx: CanvasRenderingContext2D, o: RiderDrawOptions): void {
  const h = RIDER_HEIGHT * o.unitPx;
  if (h < 2) return;
  const w = h * 0.62;

  ctx.save();

  // Contact shadow shrinks and softens with air, which is the main height cue.
  const airPx = Math.max(0, o.y);
  void airPx;

  if (o.silhouette) ctx.globalAlpha *= 0.42;
  else if (o.ghostly > 0) ctx.globalAlpha *= 0.35 + 0.65 * Math.abs(Math.sin(o.timeS * 22));

  ctx.translate(o.x, o.y);

  if (o.sending) drawSendTrail(ctx, w, h, o.theme, o.timeS);

  // Trick pose: a whip yaws the bike, a flip rotates the whole rig.
  const flip = o.trickName === 'Backflip' || o.trickName === 'Tailwhip 360';
  const p = o.trickPhase;
  let rot = o.lean * 0.22;
  let skew = 0;
  if (p > 0) {
    if (flip) rot += -p * Math.PI * 2;
    else skew = Math.sin(p * Math.PI * 2) * 0.55;
  }
  if (o.stunned) rot += Math.sin(o.timeS * 30) * 0.3;

  ctx.rotate(rot);
  ctx.transform(1, 0, skew, 1, 0, 0);

  const s = o.style;
  const wheelR = h * 0.27;
  const hubY = -wheelR;

  // Front wheel, peeking out ahead of the rear one; sells the third dimension.
  ctx.fillStyle = '#0d0f18';
  ctx.beginPath();
  ctx.ellipse(0, hubY - h * 0.05, w * 0.15, wheelR * 0.86, 0, 0, Math.PI * 2);
  ctx.fill();

  // Rear wheel, seen end-on: a narrow ellipse with a rim highlight and a tyre.
  ctx.fillStyle = '#1a1d26';
  ctx.beginPath();
  ctx.ellipse(0, hubY, w * 0.23, wheelR, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = rgba('#ffffff', 0.26);
  ctx.lineWidth = Math.max(0.7, h * 0.014);
  ctx.beginPath();
  ctx.ellipse(0, hubY, w * 0.13, wheelR * 0.66, 0, 0, Math.PI * 2);
  ctx.stroke();

  // Chainstays out to the axle, then seat stays up to the saddle.
  ctx.strokeStyle = s.frame;
  ctx.lineWidth = Math.max(1.2, h * 0.05);
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(-w * 0.3, hubY + wheelR * 0.35);
  ctx.lineTo(-w * 0.12, -h * 0.46);
  ctx.lineTo(w * 0.12, -h * 0.46);
  ctx.lineTo(w * 0.3, hubY + wheelR * 0.35);
  ctx.stroke();
  // Seat tube down to the cranks.
  ctx.beginPath();
  ctx.moveTo(0, -h * 0.5);
  ctx.lineTo(0, hubY + wheelR * 0.1);
  ctx.stroke();

  // Saddle.
  ctx.fillStyle = '#1b1e28';
  roundRect(ctx, -w * 0.18, -h * 0.55, w * 0.36, h * 0.07, h * 0.03);
  ctx.fill();

  // Legs, knees out, cranking.
  ctx.strokeStyle = shade(s.kit, -0.4);
  ctx.lineWidth = Math.max(1.2, h * 0.065);
  ctx.beginPath();
  ctx.moveTo(-w * 0.11, -h * 0.5);
  ctx.lineTo(-w * 0.33, -h * 0.3);
  ctx.lineTo(-w * 0.16, -h * 0.12);
  ctx.moveTo(w * 0.11, -h * 0.5);
  ctx.lineTo(w * 0.33, -h * 0.3);
  ctx.lineTo(w * 0.16, -h * 0.12);
  ctx.stroke();

  // Torso.
  ctx.fillStyle = s.kit;
  roundRect(ctx, -w * 0.25, -h * 0.85, w * 0.5, h * 0.34, w * 0.16);
  ctx.fill();
  ctx.fillStyle = rgba('#000000', 0.18);
  roundRect(ctx, -w * 0.25, -h * 0.64, w * 0.5, h * 0.13, w * 0.1);
  ctx.fill();

  // Fork, handlebar, grips, then arms reaching to them.
  ctx.strokeStyle = shade(s.frame, -0.2);
  ctx.lineWidth = Math.max(1, h * 0.03);
  ctx.beginPath();
  ctx.moveTo(-w * 0.12, -h * 0.6);
  ctx.lineTo(-w * 0.15, hubY - wheelR * 0.2);
  ctx.moveTo(w * 0.12, -h * 0.6);
  ctx.lineTo(w * 0.15, hubY - wheelR * 0.2);
  ctx.stroke();

  ctx.strokeStyle = shade(s.frame, -0.35);
  ctx.lineWidth = Math.max(1, h * 0.035);
  ctx.beginPath();
  ctx.moveTo(-w * 0.58, -h * 0.62);
  ctx.lineTo(w * 0.58, -h * 0.62);
  ctx.stroke();
  ctx.fillStyle = s.helmet;
  ctx.fillRect(-w * 0.64, -h * 0.65, w * 0.1, h * 0.06);
  ctx.fillRect(w * 0.54, -h * 0.65, w * 0.1, h * 0.06);

  ctx.strokeStyle = s.kit;
  ctx.lineWidth = Math.max(1, h * 0.045);
  ctx.beginPath();
  ctx.moveTo(-w * 0.22, -h * 0.79);
  ctx.lineTo(-w * 0.54, -h * 0.63);
  ctx.moveTo(w * 0.22, -h * 0.79);
  ctx.lineTo(w * 0.54, -h * 0.63);
  ctx.stroke();

  // Helmet.
  ctx.fillStyle = s.helmet;
  ctx.beginPath();
  ctx.arc(0, -h * 0.93, w * 0.185, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = rgba('#0a0d16', 0.5);
  roundRect(ctx, -w * 0.185, -h * 0.97, w * 0.37, h * 0.05, h * 0.02);
  ctx.fill();
  ctx.lineCap = 'butt';

  if (o.airborne && !o.silhouette) {
    ctx.strokeStyle = rgba(o.theme.accent, 0.35 + o.speedRatio * 0.4);
    ctx.lineWidth = Math.max(0.6, h * 0.02);
    for (let i = 0; i < 3; i++) {
      const yy = -h * (0.2 + i * 0.22);
      ctx.beginPath();
      ctx.moveTo(-w * (0.8 + i * 0.1), yy);
      ctx.lineTo(-w * (1.4 + i * 0.14), yy - h * 0.04);
      ctx.moveTo(w * (0.8 + i * 0.1), yy);
      ctx.lineTo(w * (1.4 + i * 0.14), yy - h * 0.04);
      ctx.stroke();
    }
  }

  ctx.restore();
}

/** Ground shadow, drawn before the rider so it sits under the bike. */
export function drawRiderShadow(
  ctx: CanvasRenderingContext2D,
  x: number,
  groundY: number,
  unitPx: number,
  airUnits: number,
): void {
  const h = RIDER_HEIGHT * unitPx;
  const lift = Math.min(1, airUnits / 700);
  ctx.fillStyle = rgba('#000000', 0.32 * (1 - lift * 0.8));
  ctx.beginPath();
  ctx.ellipse(x, groundY, h * 0.34 * (1 - lift * 0.35), h * 0.075 * (1 - lift * 0.35), 0, 0, Math.PI * 2);
  ctx.fill();
}

function drawSendTrail(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  theme: Theme,
  timeS: number,
): void {
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  for (let i = 0; i < 4; i++) {
    const t = ((timeS * 5 + i * 0.25) % 1);
    const scale = 1 + t * 1.5;
    ctx.globalAlpha = (1 - t) * 0.4;
    const g = ctx.createRadialGradient(0, -h * 0.4, 0, 0, -h * 0.4, w * scale);
    g.addColorStop(0, rgba(theme.accent, 0.7));
    g.addColorStop(1, rgba(theme.accent2, 0));
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.ellipse(0, -h * 0.4, w * scale, h * 0.55 * scale, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}
